import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Plus, BookOpen, Loader2, Trash2, Layers, ListChecks, FileUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";

const inputCls = "w-full bg-white border-2 border-black rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-[#FFE066]/50 text-base";

export default function StudyPage() {
  const [sets, setSets] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", subject: "", text: "" });
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const prefill = location.state?.prefill;
    if (prefill) {
      setForm((f) => ({ ...f, title: prefill.title || "", subject: prefill.subject || "" }));
      setOpen(true);
    }
  }, [location.state]);

  const load = useCallback(() => api.get("/study-sets").then((r) => setSets(r.data)), []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!sets.some((s) => s.status === "processing")) return;
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, [sets, load]);

  const create = async () => {
    if (!form.title || !form.subject) {
      toast.error("Give it a topic and subject!");
      return;
    }
    setSaving(true);
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("subject", form.subject);
    fd.append("text", form.text);
    if (file) fd.append("file", file);
    try {
      await api.post("/study-sets", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Mia's building your study set — notes, flashcards and a quiz!");
      setOpen(false);
      setForm({ title: "", subject: "", text: "" });
      setFile(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create that study set.");
    }
    setSaving(false);
  };

  const remove = async (id) => {
    await api.delete(`/study-sets/${id}`);
    load();
  };

  return (
    <div className="max-w-lg mx-auto px-5 pt-8 pb-32 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold">Study</h1>
          <p className="text-gray-600 text-sm mt-1">ATAR-aligned notes, flashcards & quizzes — built by Mia from your notes or the syllabus.</p>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="new-study-set-button"
          className="shrink-0 bg-[#4D96FF] border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0px_rgba(0,0,0,1)] transition-all font-bold p-3"
        >
          <Plus size={20} strokeWidth={3} />
        </button>
      </div>

      {sets.length === 0 && (
        <div className="bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-6 text-gray-600" data-testid="study-empty-msg">
          No study sets yet. Tap + and Mia will turn any topic, your notes, or a document into notes, flashcards and a quiz.
        </div>
      )}

      {sets.map((s) => (
        <div key={s.id} className="bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-5" data-testid={`study-set-${s.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-bold text-lg leading-tight">{s.title}</p>
              <p className="text-sm text-gray-600">{s.subject}</p>
            </div>
            <button onClick={() => remove(s.id)} data-testid={`delete-study-set-${s.id}`} className="shrink-0 bg-white border-2 border-black rounded-full shadow-[2px_2px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-[2px] transition-all p-2">
              <Trash2 size={16} />
            </button>
          </div>
          {s.status === "processing" && (
            <div className="flex items-center gap-2 mt-3 text-sm text-gray-600" data-testid={`study-processing-${s.id}`}>
              <Loader2 size={16} className="animate-spin" /> Mia's building it... (~30s)
            </div>
          )}
          {s.status === "error" && <p className="mt-3 text-sm text-[#FF6B6B] font-bold">{s.error}</p>}
          {s.status === "done" && (
            <div className="flex items-center gap-3 mt-3">
              <span className="inline-flex items-center gap-1 text-xs font-bold text-gray-600"><Layers size={14} /> {s.flashcards} cards</span>
              <span className="inline-flex items-center gap-1 text-xs font-bold text-gray-600"><ListChecks size={14} /> {s.quiz} questions</span>
              <Link
                to={`/study/${s.id}`}
                data-testid={`open-study-set-${s.id}`}
                className="ml-auto bg-[#FFE066] border-2 border-black rounded-full shadow-[3px_3px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-[2px] transition-all font-bold px-4 py-1.5 text-sm flex items-center gap-1.5"
              >
                <BookOpen size={15} /> Study
              </Link>
            </div>
          )}
        </div>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">New study set</DialogTitle>
            <DialogDescription>Paste class notes, attach a doc, or just name the topic — Mia handles the rest.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input className={inputCls} placeholder="Topic (e.g. Coastal erosion)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="study-title-input" />
            <input className={inputCls} placeholder="Subject (e.g. Geography)" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="study-subject-input" />
            <textarea className={inputCls} rows={3} placeholder="Paste your notes here (optional)" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="study-text-input" />
            <button
              onClick={() => fileRef.current?.click()}
              data-testid="study-file-button"
              className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-black rounded-xl py-3 text-sm font-bold bg-white hover:bg-[#FFE066]/20 transition-colors"
            >
              <FileUp size={16} /> {file ? file.name : "Attach a PDF / photo (optional)"}
            </button>
            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.txt" className="hidden" data-testid="study-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <button
              onClick={create}
              disabled={saving}
              data-testid="study-create-button"
              className="w-full bg-[#FFE066] border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-1 transition-all font-bold py-3 disabled:opacity-60"
            >
              {saving ? "Starting..." : "Build my study set"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
